import React, { useState, useEffect } from "react";

import { Trans } from "react-i18next";
import { Link, useHistory, useParams } from "react-router-dom";

import { getMappedFilters, getSessionFilters } from "../../utils/FilterHelpers";
import FilterDropdown from "../../components/Dropdown/FilterDropdown";
import { getCurrentDateYMD } from "../../utils/DateHelper";
import { PRODUCTS_STATUS, ROLES, BOOLEAN_OPTIONS } from "../../utils/constants";
import LoadingOverlay from "../../components/Loaders/LoadingOverlay";
import CardTable from "../../components/Tables/CardTable";
import Pagination from "../../components/Pagination/Pagination";
import { useTranslation } from "react-i18next";
import ProductVersionAPI from "../../API/productVersion.api";
import { longDateTime } from "../../utils/DateHelper";
import { productStatusFormatter } from "../../utils/TagsFormatters";
import ProductTags from "../../components/Tags/ProductTags";
import { reformatCountry } from "../../utils/CountryHelper";
import ActionsDropdown from "../../components/Dropdown/ActionsDropdown";
import { isAuthorized } from "../../utils/RolesHelpers";
import { getRoles } from "../../utils/OktaHelpers";
import { ErrorAlert, SuccessAlert } from "../../utils/Alerts";
import LogCapture from "../../utils/LogManagement/LogHelper";
import BreadCrumb from "../../components/Navbars/BreadCrumb";
import BaseAlertModal from "../../components/Modals/BaseAlertModal";
import { isEmpty } from "lodash";
import { setObject, getObject } from "../../utils/StorageHelpers";
import MissingData from "../../components/Shared/MissingData";
import SimpleNotification from "../../components/Notifications/SimpleNotification";
import { handleLink } from "../../utils/ExternalLinksHelper";

const PublishedProductVersions = () => {
    //Data States
    const [productVersions, setProductVersions] = useState(null);
    const [activatedVersionIndex, setActivatedVersionIndex] = useState(null);
    const [selectedVersion, setSelectedVersion] = useState({});

    //General States
    const [isLoading, setIsLoading] = useState(false);
    const [linkedBundlesModal, setLinkedBundlesModal] = useState(false);
    const [bundles, setBundles] = useState([]);
    const [confirmationModal, setConfirmationModal] = useState(false);
    const [isConfirmationModalLoading, setIsConfirmationModalLoading] =
        useState(false);
    const [deleteModal, setDeleteModal] = useState(false);

    //Pagination States
    const [currentPage, setCurrentPage] = useState(1);
    const [versionsTotalCount, setVersionsTotalCount] = useState(0);

    //Filter States
    const [filters, setFilters] = useState({});
    const [filtersUsed, setFiltersUsed] = useState(false);
    const [appliedFilters, setAppliedFilters] = useState(() =>
        getSessionFilters("published-products-filters")
    );
    const [sortBy, setSortBy] = useState(null);
    const [sortDirection, setSortDirection] = useState(null);

    //Hooks
    const history = useHistory();
    const params = useParams();
    const { t } = useTranslation();

    const [itemsPerRequest, setItemsPerRequest] = useState(
        () => getObject("Published-Product-Version-itemsPerPage", true) || 10
    );

    let productId = params?.id;

    const currentPageDetails = (row) => ({
        label: t("productPlaceholderVersion", {
            productName: row.productName,
        }),
        path: `/product/version/list/${row.productId}`,
    });

    //Consts
    const searchFilters = [
        {
            label: t("versionName"),
            name: "version",
            type: "Input",
            placeholder: t("inputPlaceholder", { entity: t("versionName") }),
        },
        {
            label: t("active"),
            name: "active",
            type: "Dropdown",
            options: Object.values(BOOLEAN_OPTIONS).map((val) => ({
                label: t(val),
                value: val,
            })),
        },
        {
            label: t("status"),
            name: "status",
            type: "Dropdown",
            options: Object.values(PRODUCTS_STATUS).map((status) => ({
                label: status,
                value: status,
            })),
        },
        {
            label: t("publishedAt"),
            name: "publishedAt",
            type: "Date",
            max: getCurrentDateYMD(),
        },
    ];

    const actionsDropdownOptions = (row, index) => [
        {
            name: t("review"),
            icon: "fas fa-search",
            authorized: isAuthorized([ROLES.support], getRoles()),
            handler: () =>
                history.push({
                    pathname: "/product/review",
                    state: {
                        productVersionId: row.id,
                        previousPage: currentPageDetails(row),
                    },
                }),
        },
        {
            name: t("edit"),
            icon: "fas fa-pen",
            authorized: true,
            handler: () =>
                history.push({
                    pathname: `/product/version/update/${row.id}`,
                    state: {
                        versionId: row.id,
                        publishedModelId: row.productId,
                        isBundle: row.isBundle,
                    },
                }),
            isDisabled: () => !row.canEdit,
        },
        {
            name: t("comment"),
            icon: "fas fa-comments",
            authorized: true,
            handler: () =>
                history.push({
                    pathname: "/comments",
                    state: {
                        publishedModel: { ...row, productVersion: row.version },
                        productVersionId: row.id,
                        previousPage: currentPageDetails(row),
                    },
                }),
        },
        {
            name: t("delete"),
            icon: "fas fa-trash",
            authorized: true,
            includeHorizontalLine: true,
            color: "red",
            handler: () => {
                setDeleteModal(true);
                setSelectedVersion({
                    version: row,
                    index,
                });
            },
            isDisabled: () => !row.canDelete,
        },
    ];

    const keys = [
        {
            name: "active",
            element: (row, index) => {
                const isActive = activatedVersionIndex === index;
                const isDisabled =
                    row.status !== PRODUCTS_STATUS.accepted ? "disabled" : "";
                return (
                    <i
                        className={` ${
                            isActive
                                ? `fas fa-check-square success ${isDisabled}`
                                : `far fa-square cursor-pointer ${isDisabled}`
                        } text-lg`}
                        onClick={() => {
                            if (isActive) return;
                            setSelectedVersion({
                                version: row,
                                index,
                            });
                            setConfirmationModal(true);
                        }}
                    />
                );
            },
        },
        { name: "productName" },
        { name: "version" },
        { name: "description" },
        { name: "createdBy" },
        {
            name: "status",
            element: (row) => productStatusFormatter(row.status),
        },
        {
            name: "supportedCountries",
            element: (row) => (
                <div className="w-40">
                    <ProductTags
                        tags={row.supportedCountries?.map((c) =>
                            reformatCountry(c)
                        )}
                        center={true}
                        limit={2}
                    />
                </div>
            ),
        },
        { name: "publishedAt", parseMethod: longDateTime },
        {
            element: (row, index) => {
                return (
                    <ActionsDropdown
                        attributes={actionsDropdownOptions(row, index)}
                        row={row}
                    />
                );
            },
        },
    ];

    const headers = [
        { label: t("active"), name: "active", skipSort: true },
        { label: t("productName"), name: "productName", skipSort: true },
        { label: t("versionName"), name: "version" },
        {
            label: t("versionDescription"),
            name: "description",
            skipSort: true,
        },
        { label: t("createdBy"), name: "createdBy", skipSort: true },
        { label: t("status"), name: "status", skipSort: true },
        {
            label: t("supportedCountries"),
            name: "supportedCountries",
            skipSort: true,
        },
        { label: t("publishedAt"), name: "publishedAt" },
        { label: t("actions"), name: "actions", skipSort: true },
    ];

    const breadCrumbs = [
        {
            path: "/dashboard",
            element: () => <i className="fas fa-home" />,
        },
        {
            path: "/published-products",
            element: () => (
                <Trans i18nKey="publishedProducts">Published Products</Trans>
            ),
        },
        {
            element: () => (
                <Trans i18nKey="productVersions">Product Versions</Trans>
            ),
        },
    ];

    useEffect(() => {
        if (!productId) {
            history.push("/page-not-found");
        }

        return () => {};
    }, []);

    useEffect(() => {
        if (sortBy && sortDirection) {
            setCurrentPage(1);
            fetchProductVersions(appliedFilters, sortBy, sortDirection);
        } else {
            setCurrentPage(1);
            fetchProductVersions(appliedFilters);
        }

        return () => {};
    }, [sortBy, sortDirection]);

    const handleActivateClicked = async (row, index) => {
        setIsConfirmationModalLoading(true);
        const result = await ProductVersionAPI.activateVersion(row.id);
        if (result?.modified) {
            if (result?.data) {
                setLinkedBundlesModal(true);
                setBundles(result?.data?.bundles);
            }
            setActivatedVersionIndex(index);
            setConfirmationModal(false);
            setSelectedVersion({});
            SuccessAlert(t("versionActivationSuccess"));
            LogCapture.info(
                `Version ${row.version} of product ${row.productName} activated`
            );
        } else {
            ErrorAlert(t("versionActivationFailure"));
            LogCapture.error(
                `Couldn't activate verison ${row.version} of product ${row.productName}`
            );
        }
        setIsConfirmationModalLoading(false);
    };

    const handleDeleteProductVersion = async (row) => {
        const result = await ProductVersionAPI.removeProductVersion(row.id);

        if (result?.deleted) {
            setSelectedVersion({});
            setCurrentPage(1);
            setDeleteModal(false);
            productVersions.length === 1
                ? history.push("/published-products")
                : fetchProductVersions(appliedFilters, sortBy, sortDirection);
            SuccessAlert(
                t("deleteSuccessMessage", { entity: t("productVersion") })
            );
            LogCapture.info(
                `Version ${row.version} of product ${row.productName} deleted`
            );
        } else {
            LogCapture.error(
                `Couldn't delete verison ${row.version} of product ${row.productName}`
            );
        }
    };

    const fetchProductVersions = async (
        filters,
        defaultSortBy,
        defaultSortDirection,
        offset,
        itemsPerPage = itemsPerRequest
    ) => {
        setIsLoading(true);
        const result = await ProductVersionAPI.listProductVersions(
            productId,
            itemsPerPage,
            getMappedFilters(filters, ["active"]),
            offset,
            defaultSortBy,
            defaultSortDirection
        );
        if (!result?.data) {
            history.push("/page-not-found");
        } else {
            setProductVersions(result.data);
            setActivatedVersionIndex(
                result.data.findIndex((version) => version.active)
            );
            setVersionsTotalCount(result.totalCount);
        }
        setIsLoading(false);
    };

    const applyFilters = (specificAppliedFilters) => {
        fetchProductVersions(specificAppliedFilters, sortBy, sortDirection);
        setFiltersUsed(true);
    };

    const handlePageClick = (index) => {
        if (index !== currentPage) {
            setCurrentPage(index);
            fetchProductVersions(filters, sortBy, sortDirection, index - 1);
        }
    };

    const handleItemsPerPage = (itemsPerPage) => {
        setItemsPerRequest(itemsPerPage);
        setCurrentPage(1);
        setObject("Published-Product-Version-itemsPerPage", itemsPerPage, true);
        // call API to update the items per page
        fetchProductVersions(filters, sortBy, sortDirection, 0, itemsPerPage);
    };

    const isNewVersionDisabled = () => {
        return activatedVersionIndex === null ||
            productVersions[activatedVersionIndex]?.status ===
                PRODUCTS_STATUS.underReview
            ? "disabled"
            : "";
    };

    const rednerListItems = () => {
        return bundles.map((bundle) => {
            return (
                <li key={bundle.id}>
                    <span
                        onClick={() => {
                            handleLink(`/product/version/list/${bundle.id}`);
                        }}
                        className="inline underline text-blue-600 flex items-center gap-2 cursor-pointer text-xs"
                    >
                        {bundle.name}
                        <i className="fas fa-external-link-square-alt text-xxs text-primary-color-blue"></i>
                    </span>
                </li>
            );
        });
    };

    return (
        <>
            <BreadCrumb crumbs={breadCrumbs} />
            <header className="main-content-header flex items-center justify-between">
                <h1 className="h1">
                    <Trans i18nKey="productVersions">Product Versions</Trans>
                </h1>
                <div className="action-header-wrapper flex items-center">
                    <FilterDropdown
                        filterOptions={searchFilters}
                        filters={filters}
                        setFilters={setFilters}
                        appliedFilters={appliedFilters}
                        setAppliedFilters={setAppliedFilters}
                        component="published-products"
                        applyFilters={applyFilters}
                    />
                    <button
                        className={`default-orange-outline-btn ml-2 ${isNewVersionDisabled()}`}
                    >
                        <Link
                            to={{
                                pathname: "/product/version/create",
                                state: {
                                    publishedModelId:
                                        !isEmpty(productVersions) &&
                                        productVersions[0]?.productId,
                                    isBundle:
                                        !isEmpty(productVersions) &&
                                        productVersions[0]?.isBundle,
                                    versionId:
                                        productVersions?.[activatedVersionIndex]
                                            ?.id,
                                },
                            }}
                        >
                            <i className="fas fa-plus mr-2" />
                            <Trans i18nKey="newVersion">New Version</Trans>
                        </Link>
                    </button>
                </div>
            </header>
            <LoadingOverlay active={isLoading}>
                <div className="card-table shadow">
                    {versionsTotalCount > 0 ? (
                        <>
                            <CardTable
                                headers={headers}
                                rows={productVersions}
                                purpose={"published-products"}
                                keys={keys}
                                sortBy={sortBy}
                                setSortBy={setSortBy}
                                sortDirection={sortDirection}
                                setSortDirection={setSortDirection}
                                actionColumn={false}
                                ignoreFirstChild={true}
                                hasDeleteAccess={false}
                                hasEditAccess={false}
                            />
                            <Pagination
                                currentPage={currentPage}
                                setCurrentPage={handlePageClick}
                                totalCount={versionsTotalCount}
                                sizePerPage={itemsPerRequest}
                                handleItemsPerPage={handleItemsPerPage}
                            />
                        </>
                    ) : (
                        <MissingData
                            isFilterUsed={filtersUsed}
                            entity={t("productVersions")}
                            includeText={!isLoading}
                        />
                    )}
                </div>
            </LoadingOverlay>

            <BaseAlertModal
                isVisible={deleteModal}
                setIsVisible={setDeleteModal}
                title={t("deleteTitle", {
                    purpose: t("productVersions"),
                })}
                message={t("deleteMessage", {
                    purpose: t("productVersions"),
                })}
                action={{
                    submitAction: () =>
                        handleDeleteProductVersion(selectedVersion.version),
                }}
                type="delete"
            />

            <BaseAlertModal
                isVisible={linkedBundlesModal}
                setIsVisible={setLinkedBundlesModal}
                iconClassName="fas fa-exclamation-triangle"
                title={t("bundleAffected")}
                message={t("bundlesModalBody")}
                includeSubmit={false}
            >
                <div className="p-4 w-full">
                    <ul className="list-disc text-primary-color-blue h-28 overflow-auto px-12 py-4 bg-white-smoke">
                        {rednerListItems()}
                    </ul>
                </div>
            </BaseAlertModal>
            <BaseAlertModal
                isVisible={confirmationModal}
                setIsVisible={setConfirmationModal}
                title={t("activateVersionConfirmationTitle", {
                    version: selectedVersion.version?.version,
                })}
                isSubmitting={isConfirmationModalLoading}
                message={t("activateVersionConfirmationMessage")}
                action={{
                    submitAction: () =>
                        handleActivateClicked(
                            selectedVersion.version,
                            selectedVersion.index
                        ),
                }}
                type="confirmation"
            >
                {selectedVersion?.version?.forceUpgrade && (
                    <SimpleNotification
                        message={t("forceUpgradeReview")}
                        type="warn"
                        className="w-full mt-3 text-center"
                        textClassName="w-full"
                    />
                )}
            </BaseAlertModal>
        </>
    );
};

export default PublishedProductVersions;
